/**
 * Where a span of a document is in its source PDF (ingestion.md IN-6, build plan C4). A
 * converter may store a page map in the document metadata, `pages`: compact JSON
 * text with, per page, the PDF page (1-based), the printed page label (or `""`) and the
 * code-point offset where that page's text starts. Any offset maps to its page by a binary
 * search. Documents without a valid map have no pages; nothing is guessed.
 */

export interface PageStart {
  readonly pdfPage: number;
  /** The printed page label, such as `28` or `iv`; `""` if unknown. */
  readonly label: string;
  /** Code points from the start of the document text. */
  readonly offset: number;
}

export const PAGES_METADATA_KEY = "pages";
export const SOURCE_URL_METADATA_KEY = "source_url";

/** The page map of a document's metadata, ordered by offset; `null` if it has none or it is invalid. */
export function pageMapOf(metadata: Readonly<Record<string, string>>): readonly PageStart[] | null {
  const text = metadata[PAGES_METADATA_KEY];
  if (text === undefined || text.trim() === "") {
    return null;
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  const pages: PageStart[] = [];
  for (const entry of value as readonly unknown[]) {
    if (!Array.isArray(entry) || entry.length !== 3) {
      return null;
    }
    const [pdfPage, label, offset] = entry as readonly unknown[];
    if (!isWholeNumber(pdfPage) || pdfPage < 1 || typeof label !== "string" || !isWholeNumber(offset) || offset < 0) {
      return null;
    }
    pages.push({ pdfPage, label, offset });
  }
  return pages.sort((left, right) => left.offset - right.offset);
}

/** The page an offset falls on: the last page that starts at or before it. */
export function pageAt(pages: readonly PageStart[], offset: number): PageStart | null {
  let low = 0;
  let high = pages.length - 1;
  let found: PageStart | null = null;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const page = pages[middle];
    if (page === undefined) {
      break;
    }
    if (page.offset <= offset) {
      found = page;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return found;
}

/** The pages a span `[start, end)` covers, first and last; one page when they are the same. */
export function pagesOfSpan(pages: readonly PageStart[], start: number, end: number): { readonly first: PageStart; readonly last: PageStart } | null {
  const first = pageAt(pages, start);
  const last = pageAt(pages, Math.max(start, end - 1));
  return first === null || last === null ? null : { first, last };
}

/**
 * The public PDF opened at a page (`#page=30`, RFC 8118's open parameter, which browsers'
 * PDF viewers follow). Only `http` and `https` sources are linked.
 */
export function pdfPageUrl(sourceUrl: string | undefined, pdfPage: number): string | null {
  if (sourceUrl === undefined) {
    return null;
  }
  try {
    const url = new URL(sourceUrl);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return null;
    }
    url.hash = `page=${String(pdfPage)}`;
    return url.toString();
  } catch {
    return null;
  }
}

function isWholeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value);
}
